import toast from 'react-hot-toast'
import { AxiosError } from 'axios'

import {
  ASN,
  Batch,
  Case,
  Container,
  FreightShipment,
  Generic,
  Pallet,
  ParcelShipment,
  Place,
  Product,
  UnitOfMeasure,
} from 'src/api/models'
import { skuit } from 'src/services/skuit'
import { Action, ApplyScan, Mode } from './interface'
import { Handler, HandlerInput } from './base'

export enum FoundEvent {
  Save,
}

export type FoundHandlerCallback = (data: any) => void

const getKey = (product: Generic, sku?: Generic, productCase?: Case, batch?: Batch) => {
  return `${product.object.guid}-${sku?.object?.guid}-${productCase?.guid}-${batch?.guid}`
}

interface FoundItemArgs {
  product: Generic
  sku?: Generic
  skus: Generic[]
  productCase?: Case
  batch?: Batch
}

export class FoundItem {
  product: Generic
  sku?: Generic
  skus: Generic[]
  productCase?: Case
  batch?: Batch
  count: number

  constructor(item: FoundItemArgs) {
    this.product = item.product
    this.sku = item.sku
    this.skus = item.skus
    this.productCase = item.productCase
    this.batch = item.batch
    this.count = 0
  }

  get key() {
    return getKey(this.product, this.sku, this.productCase, this.batch)
  }
}

class FoundItemList {
  items: FoundItem[]

  constructor() {
    this.items = []
  }

  move = (item: FoundItem, mode: Mode, count: number = 1) => {
    const key = item.key
    const index = this.items.map((g) => g.key).indexOf(key)
    let oldItem: FoundItem
    if (index > -1) {
      oldItem = this.items[index]
      item.count = oldItem.count
      this.items.splice(index, 1)
    }
    if (mode === Mode.Scan) {
      item.count += count
    } else {
      item.count -= count
    }
    if (item.count !== 0) {
      this.items.splice(0, 0, item)
    }
  }
}

interface FoundHanlderInput extends HandlerInput {
  source: Generic
}

export class FoundHandler extends Handler {
  source: Generic
  inventory: FoundItemList
  mode: Mode
  callbacks: FoundHandlerCallback[]
  lastSkuMap: { [product: string]: Generic | undefined }
  lastCaseMap: { [product: string]: Case | undefined }
  lastBatchMap: { [product: string]: Batch | undefined }
  toMeasure?: FoundItemArgs
  toResolve?: FoundItemArgs

  constructor(props: FoundHanlderInput) {
    super(props)
    this.source = props.source
    this.mode = Mode.Scan
    this.inventory = new FoundItemList()
    this.callbacks = []
    this.lastSkuMap = {}
    this.lastCaseMap = {}
    this.lastBatchMap = {}
  }

  apply = ({ scanned, context, local }: ApplyScan): [Action, any | undefined] => {
    switch (scanned.object.type) {
      case Case.type:
      case Product.type: {
        const product = context?.product || scanned
        const sku =
          this.lastSkuMap[product.object.guid] ||
          (context?.skus?.length === 1 ? context.skus[0] : context?.sku)
        const skus = context?.skus || (sku ? [sku] : [])
        const productCase =
          scanned.objectType === Case.type ? scanned.object : this.lastCaseMap[product.object.guid]
        if (!sku) {
          this.toResolve = {
            product,
            sku,
            skus,
            productCase,
          }
        } else if (context?.sku?.object.unitOfMeasure === UnitOfMeasure.lbs) {
          if (local?.quantity) {
            this.inventory.move(
              new FoundItem({
                product,
                sku,
                skus,
                productCase,
              }),
              this.mode,
              local.quantity
            )
          } else {
            this.toMeasure = {
              product,
              sku,
              skus,
              productCase,
            }
          }
        } else {
          this.inventory.move(
            new FoundItem({
              product,
              sku,
              skus,
              productCase,
            }),
            this.mode
          )
        }
        this.notify({})
        return [Action.None, undefined]
      }
      case Container.type:
      case Pallet.type:
      case Place.type:
      case ASN.type:
      case FreightShipment.type:
      case ParcelShipment.type: {
        const inventory = this.inventory.items.map((item) => {
          return {
            item: item.productCase?.guid || item.product?.object.guid,
            num: item.count,
          }
        })
        return [
          Action.Found,
          { source: this.source.object.guid, items: inventory, destination: scanned.object.guid },
        ]
      }
    }
    return [Action.Invalid, undefined]
  }

  setSku = (sku: Generic) => {
    if (this.toResolve) {
      this.toResolve.sku = sku
      this.inventory.move(new FoundItem(this.toResolve), this.mode)
      this.lastSkuMap[sku.object.product.guid] = sku
      this.toResolve = undefined
      this.notify({})
    }
  }

  caseSelector = (item: FoundItem) => {
    return (from: Product | Case, to: Product | Case) => {
      const oppositeMode = this.mode === Mode.Scan ? Mode.Unscan : Mode.Scan
      this.inventory.move(item, oppositeMode)
      const productCase = to.type === Case.type ? to as Case : undefined
      const newItem = new FoundItem({ product: item.product, sku: item.sku, skus: item.skus, productCase })
      this.inventory.move(newItem, this.mode)
      this.lastCaseMap[item.product.object.guid] = productCase
      this.notify({})
    }
  }

  batchSelector = (item: FoundItem) => {
    return (from: Batch | undefined, to: Batch) => {
      const oppositeMode = this.mode === Mode.Scan ? Mode.Unscan : Mode.Scan
      this.inventory.move(item, oppositeMode)
      const batch = to?.type === Batch.type ? (to as Batch) : undefined
      const newItem = new FoundItem({
        product: item.product,
        sku: item.sku,
        skus: item.skus,
        productCase: item.productCase,
        batch: batch,
      })
      this.inventory.move(newItem, this.mode)
      this.lastBatchMap[item.product.object.guid] = batch
      this.notify({})
    }
  }

  provideMeasure = (item: FoundItemArgs) => {
    return (measure: number) => {
      this.inventory.move(new FoundItem(item), this.mode, measure)
      this.toMeasure = undefined
      this.notify({})
    }
  }

  setMode = (mode: Mode) => {
    this.mode = mode
  }

  getContext = () => {
    return { activity: 'finding', source: this.source.object.guid }
  }

  registerCb = (cb: FoundHandlerCallback) => {
    this.callbacks.push(cb)
  }

  removeCb = (cb: FoundHandlerCallback) => {
    const index = this.callbacks.indexOf(cb)
    if (index > -1) {
      this.callbacks.splice(index, 1)
    }
  }

  notify = (data: any) => {
    this.callbacks.map((cb) => cb(data))
  }

  save = () => {
    this.notify({ event: FoundEvent.Save, saving: true })
    const inventory = this.inventory.items.map((item) => {
      return {
        item: item.productCase?.guid || item.product?.object.guid,
        sku: item.sku?.object.guid,
        count: item.count,
        batch: item.batch?.guid,
      }
    })
    if (this.source.object.type === Place.type) {
      skuit.sc.places
        .found({ guid: this.source.object.guid, data: inventory })
        .then(() => {
          this.inventory = new FoundItemList()
          toast.success(`Found ${inventory.length} items.`)
        })
        .catch((error: AxiosError) => {
          toast.error(
            `Failed to mark ${inventory.length} items as found: "${JSON.stringify(
              error?.response?.data || ''
            )}"`
          )
        })
        .finally(() => {
          this.notify({ event: FoundEvent.Save, saving: false })
        })
    } else {
      skuit.sc.containers
        .found({ guid: this.source.object.guid, data: inventory })
        .then(() => {
          this.inventory = new FoundItemList()
          toast.success(`Found ${inventory.length} items.`)
        })
        .catch((error: AxiosError) => {
          toast.error(
            `Failed to mark ${inventory.length} items as found: "${JSON.stringify(
              error?.response?.data || ''
            )}"`
          )
        })
        .finally(() => {
          this.notify({ event: FoundEvent.Save, saving: false })
        })
    }
  }
}
